import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import Chatbot from "../pages/Chatbot";

/**
 * Floating chatbot widget: fixed panel at the bottom with full Chatbot inside.
 * Rendered via portal so it appears above the rest of the app.
 *
 * @param {string} scriptId - Script to load when widget opens
 * @param {"none"|"image"|"video"|"voice"} avatarMode - "none" | "image" | "video" | "voice"
 *   - "voice" = streaming avatar + voice-only input, subtitle-style display (no chat box, no text input)
 * @param {boolean} embedMode - If true, load script via public embed API (no auth)
 * @param {function} onClose - Called when widget is closed
 * @param {string} [widgetTitle] - Header title (e.g. "Chat with Maya")
 * @param {string} [primaryColor] - Primary theme colour (hex, e.g. "#459EA3")
 * @param {object|null} [initialScriptData] - Preloaded script document for embed mode
 * @param {string|null} [embedToken] - Short-lived token authorizing this embed instance
 * @param {string|null} [embedParentOrigin] - Parent page origin derived from document.referrer
 */
export default function ChatbotWidget({
  scriptId,
  avatarMode = "none",
  embedMode = false,
  onClose,
  widgetTitle,
  primaryColor,
  initialScriptData = null,
  embedToken = null,
  embedParentOrigin = null,
}) {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  const voiceOnly = avatarMode === "voice";
  const effectiveAvatarMode = voiceOnly ? "video" : avatarMode;

  const panel = (
    <div
      className="fixed bottom-4 right-4 z-[9999] flex flex-col bg-black border border-gray-700 shadow-2xl overflow-hidden"
      style={{
        width: voiceOnly ? "280px" : "420px",
        height: voiceOnly ? "360px" : "min(90vh, 768px)",
        maxHeight: voiceOnly ? "360px" : "768px",
        borderRadius: voiceOnly ? "1.25rem" : "1rem",
      }}
      role="dialog"
      aria-label={voiceOnly ? "Voice chat widget" : "Chat widget"}
    >
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        <Chatbot
          widget
          scriptId={scriptId}
          widgetAvatarMode={effectiveAvatarMode}
          widgetVoiceOnly={voiceOnly}
          embedMode={embedMode}
          onClose={onClose}
          widgetTitle={widgetTitle}
          primaryColor={primaryColor}
          initialScriptData={initialScriptData}
          embedToken={embedToken}
          embedParentOrigin={embedParentOrigin}
        />
      </div>
    </div>
  );

  return createPortal(panel, document.body);
}
